import { ApplicationElement, html, render } from '@beunity/lit-components';

import Trix from 'trix/dist/trix.esm.js';
import Tribute from 'tributejs/dist/tribute.esm.js';
import '@beunity/lit-components/components/emoji_button/element.js';

import { trixTributePatchInstaller } from './lib/trix_tribute_patch.js';

import {
  addLeadingSpace,
  tributeMentionTemplate,
  hashtagParser,
  debouncedRemoteSearch,
  tributeEmojiTemplate,
  searchEmojiDB,
} from './lib/setup_trix.js';

import '../notification/element.js';

import style from './style.lit.css';

/**
 *
 * Rich text editor component based on Trix editor with emoji and mentions support
 *
 * Because Trix cannot be in shadowDom we need to use it in a slot.
 * That means that we can just wrap existing code with this component.
 *
 * ```html
 * <bu-rte>
 *  <trix-editor></trix-editor>
 * </bu-rte>
 * ```
 *
 * @prop layout - Layout of the editor (options: full, mini). Full is default and renders toolbar, mini renders only editor and is used for chat form and comments form
 * @prop submitOnEnter - Submit form on enter keypress used for chat form, comments form on Desktop
 * @prop tributeRemoteUrl - URL for remote search with user data
 */

export default class RichTextEditor extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    layout: { type: String },
    submitOnEnter: { type: Boolean },
    tributeRemoteUrl: { type: String },
  };

  constructor() {
    super();
    this.layout = 'full';
    this.trixReady = new Promise(resolve => (this.trixReadyResolve = resolve));
    this.onEmojiClick = this.onEmojiClick.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupTrixInputInSlot();
    document.addEventListener('click', this);

    this.fixChromeUnresponsiveWorkaround();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this);

    this.cleanUp();
  }

  // This hack is needed for trix to work in Chrome > 117
  // We just need to read some property on style to initialize trix in Chrome
  fixChromeUnresponsiveWorkaround() {
    requestAnimationFrame(async () => {
      await this.trixReady;
      this.trixEditor.style.display;
    });
  }

  /**
   * So menu is not visible when we switch between tabs/pages etc
   *
   * @internal
   * */
  cleanUp() {
    this.tribute?.detach(this.trixEditor);
    this.trixEditor?.remove();
  }

  handleEvent(e) {
    this.hideTributeListener(e);
  }

  get value() {
    return this.trixEditor.value;
  }

  // Yep, we need to wait for trix to initialize before we can set value
  set value(value) {
    this.trixReady.then(() => {
      this.trixEditor.value = value;
    });
  }

  get placeholder() {
    return this.trixEditor?.placeholder;
  }

  set placeholder(value) {
    this.trixReady.then(() => {
      this.trixEditor.setAttribute('placeholder', value);
    });
  }

  /** @internal */
  get trixEditor() {
    return this.querySelector('trix-editor');
  }

  /** @internal */
  get trixToolbar() {
    return this.trixEditor.toolbarElement;
  }

  /** @internal */
  get trixButtonLink() {
    return this.trixToolbar.querySelector('.trix-button--icon-link');
  }

  focus() {
    this.trixEditor.focus();
  }

  blur() {
    this.trixEditor.blur();
  }

  // Tribute bug => menu opened with showMenuForCollection method does not close on blur
  hideTributeListener(e) {
    !e.target.closest('.tribute-menu') && this.tribute?.hideMenu();
  }

  setupTribute() {
    const searchOpts = {
      pre: '<mark>',
      post: '</mark>',
    };

    const tributeMentionsOptions = {
      searchOpts,
      values: debouncedRemoteSearch(this.tributeRemoteUrl),
      menuItemTemplate: tributeMentionTemplate,
      requireLeadingSpace: true,
    };

    const tributeEmojiOptions = {
      searchOpts: { skip: true },
      trigger: ':',
      values: searchEmojiDB,
      menuItemTemplate: tributeEmojiTemplate,
      menuShowMinLength: 2,
      requireLeadingSpace: true,
    };

    // Init tribute plugin
    this.tribute = new Tribute({
      collection: [tributeMentionsOptions, tributeEmojiOptions].filter(Boolean),
      containerClass: 'tribute-menu',
      spaceSelectsMatch: true,
      noMatchTemplate: '',
    });

    // Override tribute insertTextAtCursor function as trix has it own method for inserting strings
    // This happens when we click on the tribute menu item
    this.tribute.insertTextAtCursor = text => {
      this.trixEditor.editor.insertString(text);
    };

    this.tribute.attach(this.trixEditor);

    this.tribute.range.pasteHtml = () => {};

    // Tribute events don't bubble, so we need to listen on the editor
    this.trixEditor.addEventListener('tribute-replaced', e =>
      this.onTributeReplaced(e)
    );
  }

  onEmojiClick({ detail }) {
    this.trixEditor.editor.insertString(detail.unicode);
  }

  moveCursorToEnd() {
    if (!this.value.length) return;

    this.setSelectionRange(this.value.length, this.value.length);
  }

  /**
   * If we have a value in the editor we need to move cursor to the end and remove focus
   * `set value` will be called before this function on initialize
   */
  simulateTextareaBehavior() {
    this.trixReady.then(() => {
      if (!this.value.length) return;

      this.moveCursorToEnd();
      this.blur();
    });
  }

  onTrixInitialize(e) {
    this.trixReadyResolve(true);
    this.simulateTextareaBehavior();
    // setupTrixToolbar(this.trixToolbar);
    this.renderCustomToolbar();
    this.setupTribute();
    hashtagParser(e.target.editor);
  }

  onMentionsClick() {
    addLeadingSpace(this.trixEditor.editor, 'before'); // add space before mention if necessary
    setTimeout(() => {
      this.tribute.showMenuForCollection(this.trixEditor);
    }, 0); // timeout tribute method because iOS sometimes doesn't open the menu
  }

  renderCustomToolbar() {
    const customToolbar = html`
      <span
        class="trix-button-group trix-button-group--custom-tools"
        data-trix-button-group="custom-tools">
        <ef-emoji-button @emoji-click=${this.onEmojiClick}>
          <button
            type="button"
            class="trix-button trix-button-emoji"
            data-trix-action="x-emoji"
            tabindex="-1">
            <!-- <ef-icon name="smile"></ef-icon> -->
          </button>
        </ef-emoji-button>
        <button
          @click=${e => this.onMentionsClick(e)}
          type="button"
          class="trix-button trix-button-mention"
          data-trix-action="x-mentions"
          tabindex="-1">
          <!-- <ef-icon name="at"></ef-icon> -->
        </button>
      </span>
    `;

    const customToolbarParent =
      this.trixToolbar.querySelector('.trix-button-row');

    render(customToolbar, customToolbarParent);
  }

  onTributeReplaced(e) {
    if (!e.detail.item) return;

    const { editor } = e.target;
    const { instance, item } = e.detail;
    const data = item.original;
    const { trigger } = instance.collection;

    const matchSize = instance.mentionText.length + 1;
    for (
      let i = 0;
      matchSize > 0 ? i < matchSize : i > matchSize;
      matchSize > 0 ? i++ : i--
    ) {
      editor.deleteInDirection('backward'); // Delete tribute trigger characters
    }

    if (trigger === '@') {
      const attachment = new Trix.Attachment({
        content: `<a href="${data.profile_url}" data-mention-id="${data.id}" class="mention">@${data.key}</a>`,
        contentType: 'mention',
      });
      editor.insertAttachment(attachment);
      addLeadingSpace(editor, 'after'); // add space after mention if necessary
    } else if (trigger === ':') {
      editor.insertString(data.value);
    }
  }

  onTrixChange(e) {
    hashtagParser(e.target.editor);
    this.dispatchEvent(new Event('input', { bubbles: true }));

    if (this.submitOnEnter) {
      this.closest('form')?.classList.toggle('active', e.target.value);
    }
  }

  onTrixBlur() {
    this.trixButtonLink.disabled = false;
    this.dispatchEvent(new Event('blur', { bubbles: true }));
  }

  onTrixFocus() {
    this.dispatchEvent(new Event('focus', { bubbles: true }));
  }

  onTrixSelectionChange(e) {
    // Disable link option for hashtag tags
    const isHashtagSelected = e.target.editor
      .getSelectedDocument()
      .findRangesForTextAttribute('hashtag').length;

    if (isHashtagSelected) {
      this.trixButtonLink.disabled = true;
    } else {
      this.trixButtonLink.disabled = false;
    }
  }

  onTrixAttachmentRemove() {
    // Fix for tribute bug => menu does not close when mention is deleted with delete key
    requestAnimationFrame(() => this.tribute.hideMenu());

    trixTributePatchInstaller(this.trixEditor);
  }

  onTrixAttachmentAdd(e) {
    if (e.attachment.attachment.attributes.values.contentType === 'mention')
      return;
    e.attachment.remove(); // disable image paste
  }

  onTrixFileAccept(e) {
    e.preventDefault(); // disable drag & drop image
  }

  onTrixKeypress(e) {
    if (this.submitOnEnter && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      this.closest('form')?.dispatchEvent(
        new Event('submit', { bubbles: true, cancelable: true })
      );
    }
  }

  setupTrixInputInSlot() {
    this.trixInput = this.querySelector('input, textarea');
    if (!this.trixInput) return;

    this.trixInput.hidden = true;
  }

  setSelectionRange(start, end = start) {
    const { editor } = this.trixEditor;
    const max = editor.getDocument().toString().length - 1;
    editor.setSelectedRange([start > max ? max : start, end > max ? max : end]);
  }

  render() {
    return html`
      <slot
        @trix-focus=${this.onTrixFocus}
        @trix-blur=${this.onTrixBlur}
        @trix-selection-change=${this.onTrixSelectionChange}
        @trix-attachment-remove=${this.onTrixAttachmentRemove}
        @trix-attachment-add=${this.onTrixAttachmentAdd}
        @trix-file-accept=${this.onTrixFileAccept}
        @trix-action-invoke=${this.onTrixActionInvoke}
        @trix-change=${this.onTrixChange}
        @keypress=${this.onTrixKeypress}
        @trix-initialize=${this.onTrixInitialize}
        @slotchange=${this.onSlotChange}></slot>
    `;
  }
}

RichTextEditor.registerTagName('bu-rte');

customElements.get('bu-rte') || customElements.define('bu-rte', RichTextEditor);
